import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, useBlocker } from "react-router";
import { FormProvider, useForm } from "react-hook-form";

import { Stack } from "types/generated";
import { showBlockNavigationModal } from "hooks/useBlockNavigationModal/BlockNavigationModal";

import StackTaskForm from "./Form";

type NewTaskFormProps = {
  stackId: Stack["id"];
  accessErrorMessage?: string;
  redesigned?: boolean;
};

const NewTaskForm = ({ stackId, accessErrorMessage, redesigned }: NewTaskFormProps) => {
  const form = useForm({
    mode: "onChange",
    defaultValues: {
      commandValue: "",
      skipInitialization: false,
    },
  });
  const { watch } = form;
  const commandValue = watch("commandValue");

  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !nextUrl && !!commandValue && currentLocation.pathname !== nextLocation.pathname
  );

  const blockerRef = useRef(blocker);
  useLayoutEffect(() => {
    blockerRef.current = blocker;
  });

  const onNavigationConfirm = useCallback(() => {
    if (blockerRef.current.state === "blocked") {
      blockerRef.current.proceed();
    }
  }, []);

  const onNavigationCancel = useCallback(() => {
    if (blockerRef.current.state === "blocked") {
      blockerRef.current.reset();
    }
  }, []);

  useEffect(() => {
    if (blocker.state !== "blocked") return;

    if (nextUrl) {
      blocker.proceed();
    } else {
      showBlockNavigationModal({
        onConfirm: onNavigationConfirm,
        onCancel: onNavigationCancel,
      });
    }
  }, [blocker, onNavigationConfirm, onNavigationCancel, nextUrl]);

  if (nextUrl) {
    return <Navigate to={nextUrl} replace />;
  }

  return (
    <FormProvider {...form}>
      <StackTaskForm
        setNextUrl={setNextUrl}
        stackId={stackId}
        accessErrorMessage={accessErrorMessage}
        redesigned={redesigned}
      />
    </FormProvider>
  );
};

export default NewTaskForm;
